import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../apiHelper';
import {
  useCreate,
  useEdit,
  useSingleFetch,
  useFetch_old,
  useFetch,
  usePost,
  QueryKeyT,
} from '../react-query';
import { examsQueryKeys } from './studies-query-keys';
import * as url from '../../urlHelper';
import { AxiosResponse } from 'axios';

const EXAMS_LIST_QUERY_DEFAULTS = {
  refetchOnWindowFocus: false,
  retry: 0,
} as const;

/** Paginated exams: filters JSON + sort as plain query params (clearer URLs and caching). */
export interface ExamsListQuery {
  filters: {
    patient_filters: Record<string, unknown>;
    exam_filters: Record<string, unknown>;
  };
  sort_by: string;
  sort_order: 'asc' | 'desc';
}

export interface ExamListPredictionState {
  unpredicted: number;
  total: number;
  predicted: number;
  inprogress: number;
  failed: number;
  blind_sweep_complete?: boolean;
}

export interface ExamListSeries {
  serie_instance_uid: string;
  type: string;
  prediction_state?: ExamListPredictionState;
}

export interface ExamListCreator {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export interface ExamListPatient {
  patient_id?: string | null;
  patient_name?: string | null;
  patient_last_name?: string | null;
  birth_date?: string | null;
}

export interface ExamListPregnancy {
  pregnancy_status?: string | null;
  last_menstrual_period?: string | null;
  ultrasound_date?: string | null;
  closed_at?: string | null;
  outcome_date?: string | null;
  gestational_age_weeks?: number | null;
  gestational_age_days?: number | null;
}

export interface ExamListItem {
  study_instance_uid: string;
  is_archived: boolean;
  is_locked: boolean;
  study_date?: string | null;
  created_at?: string | null;
  category?: string | null;
  creator?: ExamListCreator | null;
  patient?: ExamListPatient | null;
  pregnancy?: ExamListPregnancy | null;
  serie: ExamListSeries[];
  prediction_state: ExamListPredictionState;
}

export interface PaginatedExamListResult {
  data: ExamListItem[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
  filtered_conditions: unknown;
}

interface PaginatedExamsApiResponse {
  data: {
    result: PaginatedExamListResult;
  };
}

interface ExamResponse {
  data: any;
}

export function usePaginatedExams(
  page: number,
  itemsPerPage: number,
  query: ExamsListQuery,
  config = {}
) {
  const queryKey = [
    'paginated-exams',
    ...examsQueryKeys.pagination(page, {
      ...query,
      items_per_page: itemsPerPage,
    }),
  ];

  return useQuery<PaginatedExamListResult>({
    queryKey,
    placeholderData: (previousData) => previousData,
    queryFn: async ({ signal }) => {
      const sp = new URLSearchParams();
      sp.set('page', String(page));
      sp.set('items_per_page', String(itemsPerPage));
      sp.set('filters', JSON.stringify(query.filters));
      sp.set('sort_by', query.sort_by);
      sp.set('sort_order', query.sort_order);
      const response = await apiClient.get(`studies?${sp.toString()}`, {
        signal,
      });
      const result = (response as PaginatedExamsApiResponse | undefined)?.data?.result;
      return (
        result ?? {
          data: [],
          total_count: 0,
          has_more: false,
          page,
          items_per_page: itemsPerPage,
          filtered_conditions: null,
        }
      );
    },
    ...EXAMS_LIST_QUERY_DEFAULTS,
    ...config,
  });
}


export function useArchiveExams() {
  const archiveExamsFn = async (
    exam_ids: string[] | number[]
  ): Promise<ExamResponse> => {
    const response = await apiClient.post(`/studies/archive`, exam_ids);
    return response;
  };

  return useEdit(
    archiveExamsFn,
    examsQueryKeys.all,
    'exam.archiveFailed',
    'exam.archivedSuccess'
  );
}

export function useArchiveExam() {
  const archiveExamFn = async (
    exam_id: string | number
  ): Promise<ExamResponse> => {
    const response = await apiClient.post<ExamResponse>(
      `/studies/archive/${exam_id}`
    );
    return response;
  };

  return useEdit(
    archiveExamFn,
    ['exams', 'pagination'],
    'exam.archiveFailed',
    'exam.archivedSuccess'
  );
}

export function useUnarchiveExams() {
  const unarchiveExamsFn = async (
    exam_ids: string[] | number[]
  ): Promise<ExamResponse> => {
    const response = await apiClient.post<ExamResponse>(
      `/studies/unarchive`,
      exam_ids
    );
    return response;
  };

  return useEdit(
    unarchiveExamsFn,
    examsQueryKeys.all,
    'exam.unarchiveFailed',
    'exam.unarchivedSuccess'
  );
}

export function useUnarchiveExam() {
  const unarchiveExamFn = async (
    exam_id: string | number
  ): Promise<ExamResponse> => {
    const response = await apiClient.post<ExamResponse>(
      `/studies/unarchive/${exam_id}`
    );
    return response;
  };

  return useEdit(
    unarchiveExamFn,
    ['exams', 'pagination'],
    'exam.unarchiveFailed',
    'exam.unarchivedSuccess'
  );
}

export function useRecentStudies() {
  return useFetch(
    async () => {
      const response = await apiClient.get('/recent-studies');
      return response;
    },
    ['recent-studies'],
    {
      // refetchInterval: 60000 // Refetch every minute
    }
  );
}
export function usePatientStudies(patient_id: string) {
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `/studies/patient-studies/${patient_id}`
      );
      return response;
    },
    ['studies', 'recent-studies', patient_id],
    {
      // refetchInterval: 60000 // Refetch every minute
    }
  );
}
export function usePatientPregnancyHistory(
  patient_id: string | undefined
) {
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `/pregnancy/get-history/${patient_id}`
      );
      return response;
    },
    ['pregnancy', 'get-history', patient_id ?? ''],
    {
      enabled: Boolean(patient_id),
    }
  );
}
export function usePatientLastActivePregnancy(patient_id: string) {
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `/study_report/pregnancy/${patient_id}/measurements`
      );
      return response;
    },
    ['pregnancy', 'last-active', patient_id],
    {
      // refetchInterval: 60000 // Refetch every minute
    }
  );
}
export function useRecentStudiesForPatient(
  patientId: number | string,
  config = {}
) {
  const fetchRecentStudiesForPatient = async () => {
    return await apiClient.get(`recent_studies/${patientId}`);
  };

  return useSingleFetch(
    fetchRecentStudiesForPatient,
    examsQueryKeys.recentForPatient(patientId).map(String),
    patientId,
    config
  );
}

export const useUploadStudy = (
  updater: (oldData: any[], newData: any) => any[],
  onSuccess?: (response: any, data: any) => void,
  onError?: (error: any, data: any) => void
) => {
  return usePost<any[], any>(
    url.STUDY_ADD,
    undefined,
    updater,
    onSuccess,
    onError
  );
};

interface PredictParams {
  study_uid: string;
  series_instance_uid: string;
  sop_instance_uid: string;
}

export function usePredict() {
  const predictFn = async (params: PredictParams) => {
    const response = await apiClient.post(
      `/model_api/predict?studyUID=${params.study_uid}&study_uid=${params.study_uid}&series_instance_uid=${params.series_instance_uid}&sop_instance_uid=${params.sop_instance_uid}`,
      {}
    );
    return response.data;
  };

  return useEdit(
    predictFn,
    ['studies', 'predict'],
    'exam.predictionFailed',
    'exam.predictionStartedSuccess'
  );
}

export function useSRStatus(seriesInstanceUID: string, sopInstanceUID: string) {
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `/studies/sr-status/${seriesInstanceUID}/${sopInstanceUID}`
      );
      return response.data;
    },
    ['studies', 'sr-status', seriesInstanceUID, sopInstanceUID],
    {
      refetchInterval: (data) => {
        // Refetch every 2 seconds if status is 'INPROGRESS'
        return data?.status === 'INPROGRESS' ? 2000 : false;
      },
    }
  );
}
export function useSingleStudyServer(
  studyInstanceUID: string | number,
  config = {}
) {
  const fetchStudyServer = async () => {
    if (studyInstanceUID === -1) {
      return null;
    }
    return await apiClient.get(`/studies/${studyInstanceUID}/patient-info`);
  };
  return useSingleFetch(
    fetchStudyServer,
    ['studies', 'studies', `/studies/${studyInstanceUID}/patient-info`],
    studyInstanceUID,
    config
  );
}
interface measurementsKeys {
  studyId: string;
  serieId: string;
  instanceId: string;
}

export const getStudyMeasurements = (search: measurementsKeys) =>
  useFetch_old(
    `${url.GET_STUDIES}/${search.studyId}/series/${search.serieId}/instances/${search.instanceId}`
  );

// export const useGetStudyPredictionState = (studyId: string, queryKey?: QueryKeyT) => {
//   console.log('=========== useGetStudyPredictionState', `${url.GET_STUDIES}/prediction/${studyId}`);

//   return useFetch_old(`${url.GET_STUDIES}/prediction/${studyId}`, undefined, {
//     refetch: false,
//     refetchOnWindowFocus: false
//   }, queryKey);
// };

export const useGetStudyPredictionState = (
  studyId: string,
  queryKey?: QueryKeyT
) => {
  const key = `${url.GET_STUDIES}/prediction/${studyId}`;
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `${url.GET_STUDIES}/prediction/${studyId}`
      );
      return response;
    },
    ['prediction'], // keys are so important logique to handle the cache
    {
      refetch: false,
      refetchOnWindowFocus: false,
    }
  );
};

// export const useGetStudyPatientInfo = (studyId: string, queryKey?: QueryKeyT) => {
//   return useFetch_old(`${url.GET_STUDIES}/patient/${studyId}`, undefined, {
//     refetch: false,
//     refetchOnWindowFocus: false
//   }, queryKey);
// }

export const useGetStudyPatientInfo = (
  studyId: string,
  queryKey?: QueryKeyT
) => {
  const key = `${url.GET_STUDIES}/patient/${studyId}`;
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `${url.GET_STUDIES}/patient/${studyId}`
      );
      return response;
    },
    ['studyPatientInfo'], // keys are so important logique to handle the cache
    {
      refetch: false,
      refetchOnWindowFocus: false,
    }
  );
};

export function useCreateBlindStudy(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createBlindStudy = async (data: any) => {
    const response = await apiClient.post('/studies/create', data);
    return response;
  };

  return useCreate(
    createBlindStudy,
    ['create_blind_study'],
    'Failed to create Blind Exam',
    'Blind Exam created successfully',
    onSuccess,
    onError,
    notification
  );
}
export function useCreateRealtimeStudy(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createRealtimeStudy = async (data: any) => {
    const response = await apiClient.post('/studies/create', data);
    return response;
  };

  return useCreate(
    createRealtimeStudy,
    ['create_realtime_study'],
    'Failed to create Realtime Exam',
    'Realtime Exam created successfully',
    onSuccess,
    onError,
    notification
  );
}

export interface StudyCreateInitPayload {
  study_date?: string | Date;
  pregnancy_id?: number | null;
  appointment_id?: string | null;
  study_mode?: string | null;
  category?: string | null;
  type?: string | null;
  serie_instance_uid?: string | null;
  skip_serie_creation?: boolean;
  study_report?: Record<string, unknown> | null;
}

export function useCreateStudyInit(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createStudyInit = async (data: StudyCreateInitPayload) => {
    const response = await apiClient.post('/studies/create', data);
    return response;
  };

  return useCreate(
    createStudyInit,
    ['create_study_init'],
    'Failed to create Study',
    'Study created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useLinkStudyToPatient(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const linkStudyToPatient = async (data: any) => {
    const response = await apiClient.post(
      `/studies/${data.study_instance_uid}/link-patient`,
      {
        patient_id: data.patient_id,
        patient_name: data.patient_name,
        patient_last_name: data.patient_last_name,
      }
    );
    return response;
  };

  return useCreate(
    linkStudyToPatient,
    ['linkStudyToPatient'],
    'Failed to link study to patient',
    'Study linked to patient successfully',
    onSuccess,
    onError,
    notification
  );
}
